import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { sleep } from "@redotech/util/schedule";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export interface UsePollUpdatesProps {
  groups: FulfillmentGroupData[];
  onGroupsUpdated: (groups: FulfillmentGroupData[]) => void;
  enabled?: boolean;
}

function deduplicatedGroups(groups: FulfillmentGroupData[]) {
  const uniqueGroupsMap = new Map();

  for (const group of groups) {
    uniqueGroupsMap.set(group._id, group);
  }

  return Array.from(uniqueGroupsMap.values());
}

/**
 * Hook for polling fulfillment groups with pending rates
 * Automatically polls groups that have ratesPendingSince older than 10 seconds
 */
export function usePollUpdates({
  groups,
  onGroupsUpdated,
  enabled = true,
}: UsePollUpdatesProps): void {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);

  useLoad(
    async (signal) => {
      if (!rpcClient || !team || !enabled) {
        return;
      }

      let errorCount = 0;
      while (true) {
        try {
          // Always wait 2 seconds at the start of each loop iteration
          await sleep(Temporal.Duration.from({ seconds: 2 }), signal);

          const groupsToPoll = groups.filter(
            (group) =>
              (group.ratesPendingSince &&
                group.ratesPendingSince.getTime() < Date.now() - 10_000) ||
              (group.personalizedSlipGeneratingSince &&
                group.personalizedSlipGeneratingSince.getTime() <
                  Date.now() - 10_000),
          );

          logMissedUpdates(groupsToPoll);

          // Only make the API call if there are groups that need polling
          if (groupsToPoll.length > 0) {
            const response = await rpcClient.getReplacementGroups({
              groupIds: groupsToPoll.map(({ _id }) => _id),
            });

            const newGroups = deduplicatedGroups(
              response.replacements.map(
                (replacement) => replacement.replacementGroup,
              ),
            );

            const resolved = newGroups.filter(
              (group) => !group.ratesPendingSince,
            );
            if (resolved.length > 0) {
              datadogLogs.logger.info("Polling found group rates updates", {
                count: resolved.length,
              });
            }

            if (newGroups.length > 0) {
              onGroupsUpdated(newGroups);
            }
          }

          errorCount = 0;
        } catch (e) {
          if (signal.aborted) {
            break;
          }
          errorCount++;
          console.error("Error while polling for rate updates", e);
          await sleep(
            Temporal.Duration.from({ seconds: Math.min(errorCount, 5) }),
            signal,
          );
        }
      }
    },
    [rpcClient, team, groups, enabled],
  );
}

function logMissedUpdates(groups: FulfillmentGroupData[]) {
  const { maxLatencyGroupId, maxRatesPendingSinceMilliseconds } = groups.reduce(
    (max, group) => {
      const ratesPendingSinceMilliseconds = group.ratesPendingSince
        ? Date.now() - group.ratesPendingSince.getTime()
        : 0;
      if (
        ratesPendingSinceMilliseconds > max.maxRatesPendingSinceMilliseconds
      ) {
        return {
          maxLatencyGroupId: group._id,
          maxRatesPendingSinceMilliseconds: ratesPendingSinceMilliseconds,
        };
      }
      return max;
    },
    { maxLatencyGroupId: "", maxRatesPendingSinceMilliseconds: 0 },
  );

  // This number is just preventing log spam. The number we care about should be set in the monitor
  if (maxRatesPendingSinceMilliseconds > 15_000) {
    console.error(
      `ratesPendingSince latency: fulfillmentGroupId ${maxLatencyGroupId} latency ${maxRatesPendingSinceMilliseconds}`,
    );
  }
}
